/**
 * API の入出力契約 (contract)。
 *
 * サーバーはこのスキーマでリクエストを検証し、クライアントは同じスキーマから
 * 導出した型でレスポンスを扱う。1 箇所に定義があるので、
 * Web・React Native・バックエンドの間で型がずれない。
 */
import { z } from "zod";
import { currencyCodeSchema, expenseCategorySchema, constraintKindSchema, idSchema, nonNegativeAmountSchema, positiveAmountSchema, splitRuleSchema, MAX_GROUP_MEMBERS, } from "./types.js";
// --- 入力 -------------------------------------------------------------------
export const createGroupInputSchema = z.object({
    name: z.string().trim().min(1, "グループ名を入力してください").max(60),
    description: z.string().trim().max(300).optional(),
    currency: currencyCodeSchema.default("JPY"),
    /** 作成と同時に登録するメンバー名 */
    memberNames: z.array(z.string().trim().min(1).max(40)).max(MAX_GROUP_MEMBERS).default([]),
});
export const updateGroupInputSchema = z.object({
    name: z.string().trim().min(1).max(60).optional(),
    description: z.string().trim().max(300).nullable().optional(),
    currency: currencyCodeSchema.optional(),
});
export const createMemberInputSchema = z.object({
    name: z.string().trim().min(1, "名前を入力してください").max(40),
});
export const updateMemberInputSchema = z.object({
    name: z.string().trim().min(1).max(40),
});
export const upsertConstraintInputSchema = z.object({
    /** cover では「負担する人」。prefer / avoid では順不同 */
    memberIdA: idSchema,
    /** cover では「負担してもらう人」 */
    memberIdB: idSchema,
    kind: constraintKindSchema,
});
export const expenseInputSchema = z.object({
    title: z.string().trim().min(1, "支出名を入力してください").max(80),
    category: expenseCategorySchema.default("other"),
    amount: positiveAmountSchema,
    payers: z
        .array(z.object({ memberId: idSchema, amount: nonNegativeAmountSchema }))
        .min(1, "立て替えた人を選んでください"),
    split: splitRuleSchema,
    /** 発生日。null または省略で「未指定」になる */
    occurredOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "日付は YYYY-MM-DD 形式で指定してください")
        .nullable()
        .optional(),
    note: z.string().trim().max(500).nullable().optional(),
});
export const settlementPaymentInputSchema = z.object({
    fromMemberId: idSchema,
    toMemberId: idSchema,
    amount: positiveAmountSchema,
    settledOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    note: z.string().trim().max(200).nullable().optional(),
});
/**
 * 送金の受取先。
 *
 * ログインユーザーが自分をグループのメンバーと紐づけると、
 * そのメンバーへの送金にこの情報が表示される
 * (= 相手に口座を口頭やチャットで伝える手間がなくなる)。
 */
export const payoutAccountKindSchema = z.enum(["bank", "paypay", "other"]);
export const PAYOUT_ACCOUNT_KIND_LABELS = {
    bank: "銀行口座",
    paypay: "PayPay",
    other: "その他",
};
export const payoutAccountInputSchema = z
    .object({
    kind: payoutAccountKindSchema.default("bank"),
    bankName: z.string().trim().max(60).nullable().optional(),
    branchName: z.string().trim().max(60).nullable().optional(),
    /** ordinary (普通) | checking (当座) */
    accountType: z.enum(["ordinary", "checking"]).nullable().optional(),
    accountNumber: z.string().trim().max(20).nullable().optional(),
    accountHolder: z.string().trim().max(60).nullable().optional(),
    /** PayPay ID など */
    handle: z.string().trim().max(60).nullable().optional(),
    note: z.string().trim().max(200).nullable().optional(),
})
    .superRefine((value, ctx) => {
    if (value.kind === "bank") {
        if (!value.bankName || !value.accountNumber) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["bankName"],
                message: "銀行名と口座番号を入力してください",
            });
        }
        return;
    }
    if (!value.handle) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["handle"],
            message: value.kind === "paypay" ? "PayPay ID を入力してください" : "受取先を入力してください",
        });
    }
});
/**
 * ログインはパスワードレス (メールアドレス + ワンタイムパスワード)。
 *
 * パスワードを持たないことで、使い回し・漏洩・再設定フローがまとめて無くなる。
 * 会員登録という別の手続きも無く、初回のコード入力がそのまま登録になる。
 */
export const LOGIN_CODE_LENGTH = 6;
const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email("メールアドレスの形式が正しくありません")
    .max(200);
/** ① ワンタイムパスワードの発行を依頼する */
export const loginCodeRequestSchema = z.object({
    email: emailSchema,
});
/** ② 届いたコードで認証する。ユーザーが未登録ならここで作られる */
export const loginCodeVerifySchema = z.object({
    email: emailSchema,
    code: z
        .string()
        .trim()
        .regex(new RegExp(`^\\d{${LOGIN_CODE_LENGTH}}$`), `確認コードは ${LOGIN_CODE_LENGTH} 桁の数字です`),
});
export const suggestionQuerySchema = z.object({
    q: z.string().max(80).optional(),
    limit: z.coerce.number().int().positive().max(30).optional(),
});
export const settlementQuerySchema = z.object({
    /**
     * 制約を一切適用しないで計算する (画面の「制約を無効化」)。
     * クエリ文字列は文字列でしか来ないので、"true" だけを真として扱う
     * (z.coerce.boolean() は "false" も真になってしまう)。
     */
    ignoreConstraints: z
        .enum(["true", "false"])
        .optional()
        .transform((value) => value === "true"),
});
/** 設定を取得できないときに使う既定値。安全側 (すべて無効) に倒す */
export const DEFAULT_APP_FEATURES = { payoutAccounts: false };
//# sourceMappingURL=contracts.js.map